import { useEffect } from 'react';

interface ScheduleItem {
  id: number;
  date: string;
  day: string;
  time: string;
  doorOpen: string;
  venue: string;
  artist: string;
  title: string;
  genre: string;
  price: number;
  ticketUrl: string;
  status: string;
  description: string;
}

interface ScheduleDetailModalProps {
  item: ScheduleItem | null;
  onClose: () => void;
}

export default function ScheduleDetailModal({ item, onClose }: ScheduleDetailModalProps) {
  useEffect(() => {
    if (item) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [item]);

  if (!item) return null;

  const dateParts = item.date.split('-');

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm"></div>
      <div
        className="relative bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="relative bg-gray-900 text-white p-6 rounded-t-xl">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-white"></i>
          </button>
          <div className="flex items-center gap-2 mb-3">
            <span className="px-2.5 py-0.5 bg-white/20 text-white text-xs rounded-full font-medium whitespace-nowrap">
              {item.genre}
            </span>
            <span className="text-xs text-white/60">
              {item.status === 'on-sale' ? '発売中' : '近日発売'}
            </span>
          </div>
          <h2 className="text-xl font-bold">{item.artist} / {item.title}</h2>
        </div>

        {/* Body */}
        <div className="p-6 space-y-5">
          <p className="text-sm text-gray-600 leading-relaxed">{item.description}</p>

          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <i className="ri-calendar-line text-gray-400 w-4 h-4 flex items-center justify-center"></i>
                <span className="text-xs text-gray-500 font-medium">日程</span>
              </div>
              <p className="text-sm font-semibold text-gray-900">
                {dateParts[0]}年{parseInt(dateParts[1], 10)}月{parseInt(dateParts[2], 10)}日（{item.day}）
              </p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <i className="ri-time-line text-gray-400 w-4 h-4 flex items-center justify-center"></i>
                <span className="text-xs text-gray-500 font-medium">時間</span>
              </div>
              <p className="text-sm font-semibold text-gray-900">
                開場 {item.doorOpen} / 開演 {item.time}
              </p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <i className="ri-map-pin-2-line text-gray-400 w-4 h-4 flex items-center justify-center"></i>
                <span className="text-xs text-gray-500 font-medium">会場</span>
              </div>
              <p className="text-sm font-semibold text-gray-900">{item.venue}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <i className="ri-ticket-line text-gray-400 w-4 h-4 flex items-center justify-center"></i>
                <span className="text-xs text-gray-500 font-medium">前売チケット</span>
              </div>
              <p className="text-sm font-semibold text-gray-900">&yen;{item.price.toLocaleString()}</p>
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="flex items-start gap-2">
              <i className="ri-information-line text-amber-500 mt-0.5 w-4 h-4 flex items-center justify-center"></i>
              <div>
                <p className="text-xs text-amber-700 font-medium mb-1">チケットについて</p>
                <p className="text-xs text-amber-600 leading-relaxed">
                  チケットは公演の約1ヶ月前よりEプラス等にて販売開始予定です。詳細は各会場の公式サイトをご確認ください。別途ドリンク代が必要な場合があります。
                </p>
              </div>
            </div>
          </div>

          <div className="flex gap-3">
            <a
              href={item.ticketUrl}
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="flex-1 flex items-center justify-center gap-2 bg-gray-900 text-white py-3 rounded-lg text-sm font-semibold hover:bg-gray-800 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-ticket-2-line"></i>
              チケットを購入
            </a>
            <button
              onClick={onClose}
              className="px-6 py-3 border border-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              閉じる
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
